import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calculator, Check, Loader2, Lock, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionForm } from "@/components/FieldInput";
import { SheetView } from "@/components/SheetView";
import { StatusBadge } from "@/components/StatusBadge";
import { SignPanel } from "@/components/SignPanel";
import { ExtrasPanel } from "@/components/ExtrasPanel";
import { AuditPanel, FilesPanel } from "@/components/FilesPanel";
import { IdentityStep } from "@/components/IdentityStep";
import { MenuUpload } from "@/components/MenuUpload";
import { ShareButton, eventPhrase } from "@/components/ShareDialog";
import { PaymentsPanel } from "@/components/PaymentsPanel";
import { GuestsPanel } from "@/components/GuestsPanel";
import { ServicePanel } from "@/components/ServicePanel";
import { SuppliersPanel, TeamPanel } from "@/components/TeamPanel";
import { ExperiencePanel } from "@/components/ExperiencePanel";
import { SeatingPanel } from "@/components/SeatingPanel";
import { AvailabilityAlert } from "@/components/AvailabilityAlert";
import { useAuth } from "@/context/AuthContext";
import { CancelContractButton, CancelledBanner } from "@/components/CancelContract";
import { api, errMsg, fmtValue, loadTemplate, openPdf } from "@/lib/api";
import { PresetPicker, discountOf, presetPatch } from "@/components/MenuTools";

const n = (v) => Number(v || 0);

function calcPrices(d) {
  const gross = n(d.min_adulti) * n(d.prezzo_adulti) + n(d.min_bambini) * n(d.prezzo_bambini) + n(d.min_fornitori) * n(d.prezzo_fornitori) + n(d.servizi_importo);
  const sc = discountOf(gross, d.sconto_tipo, d.sconto_valore);
  const ref = gross - sc;
  const r = (x) => (Math.round(x * 100) / 100).toFixed(2);
  const out = { ...d, sconto_importo: sc ? r(sc) : "", prezzo_riferimento: r(ref), rata1_importo: r(ref * 0.3), rata2_importo: r(ref * 0.4), rata3_importo: r(ref - Number(r(ref * 0.3)) - Number(r(ref * 0.4))) };
  if (!d.rata3_scadenza && d.evento_data) {
    const dt = new Date(d.evento_data + "T12:00:00");
    dt.setDate(dt.getDate() - 15);
    out.rata3_scadenza = dt.toISOString().slice(0, 10);
  }
  return out;
}

function Tabs({ tabs, tab, setTab }) {
  return (
    <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="editor-tabs">
      {tabs.map(([k, label, sub]) => (
        <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
          className={`text-left whitespace-nowrap px-4 py-2.5 rounded-lg transition-colors ${tab === k ? "bg-[#0B6E8F] text-white" : "hover:bg-[#EAF5F7] text-[#0D2B3A]"}`}>
          <div className="text-sm font-medium">{label}</div>
          {sub && <div className={`text-[11px] ${tab === k ? "text-white/75" : "text-[#7B929C]"}`}>{sub}</div>}
        </button>
      ))}
    </nav>
  );
}

export default function ContractEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [data, setData] = useState({});
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "start");
  const [saveState, setSaveState] = useState("saved");
  const [version, setVersion] = useState(0);
  const timer = useRef(null);
  const dataRef = useRef({});

  const reload = useCallback(async () => {
    const r = await api.get(`/contracts/${id}`);
    setC(r.data);
    setData(r.data.data || {});
    dataRef.current = r.data.data || {};
    setVersion((v) => v + 1);
  }, [id]);

  useEffect(() => {
    loadTemplate().then(setTpl).catch((e) => toast.error(errMsg(e)));
    reload().catch((e) => toast.error(errMsg(e)));
  }, [reload]);

  const save = useCallback(async (d) => {
    setSaveState("saving");
    try {
      await api.put(`/contracts/${id}`, { data: d });
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      toast.error(errMsg(e));
    }
  }, [id]);

  const editable = c?.status === "bozza";
  const schedule = (d) => {
    dataRef.current = d;
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; save(d); }, 900);
  };
  const update = (d) => {
    setData(d);
    schedule(d);
  };
  const setFields = (patch) => {
    setData((prev) => {
      const d = { ...prev, ...patch };
      schedule(d);
      return d;
    });
  };
  const onField = (k, v) => {
    if (["sconto_tipo", "sconto_valore"].includes(k)) {
      setData((prev) => {
        const base = { ...prev, [k]: v };
        const d = base.prezzo_riferimento ? calcPrices(base) : base;
        schedule(d);
        return d;
      });
      return;
    }
    setFields({ [k]: v });
  };

  if (!c || !tpl) return <div className="text-sm text-[#7B929C]" data-testid="editor-loading">Caricamento…</div>;

  const catering = data.sede === tpl.sede_catering;
  const pages = tpl.pages.filter((p) => p.conditional !== "catering" || catering);
  const tabs = [
    ["start", "Tipo evento e identità", "Fase 1 • documenti e IA"],
    ["p1", "Scheda", "Parti, evento, prezzo"], ["cond", "Condizioni generali", "Art. 1-16 • 1341-1342"],
    ["p5", "Allegato A", "Menù concordato"], ["p6", "Allegato B", "Spazi e Piano B"],
    ...(catering ? [["p7", "Allegato C", "Catering fuori sede"]] : []),
    ["extra", "Allegato D", `Ordini extra (${(c.extras || []).length})`],
    ["firma", "Firma", "Blocco, firme, PDF"],
    ["pagamenti", "Pagamenti", "Rate, ricevute, area cliente"], ["ospiti", "Ospiti e tavoli", `Lista, allergie (${(c.guests || []).length})`], ["sala", "Mappa sala", "Tavoli, lista sposi, hostess"], ["servizio", "Scheda servizio", "Cucina, sala, costi e margine"],
    ["squadra", "Personale e scaletta", `Turni, minuto per minuto (${(c.staff || []).length})`], ["fornitori", "Fornitori e attrezzature", `Partner, materiali (${(c.suppliers || []).length})`],
    ["esperienza", "Degustazione e invitati", "Prova menù, conferme, recensioni"],
    ["files", "Documenti allegati", "Ricevute, documenti"], ["audit", "Registro attività", "Traccia completa"],
  ];
  const page = pages.find((p) => p.key === tab);

  const goTab = async (k) => {
    if (saveState === "dirty" || timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
      await save(dataRef.current);
    }
    if (["firma", "audit", "extra", "pagamenti", "ospiti", "servizio"].includes(k)) await reload();
    setTab(k);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 fade-up">
        <div>
          <Link to="/contratti" className="text-xs text-[#7B929C] inline-flex items-center gap-1 hover:text-[#0B6E8F]" data-testid="back-to-list"><ArrowLeft size={13} /> Tutti i contratti</Link>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <h1 className="font-serif-it text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="contract-title">{data.cliente_nome || "Nuovo contratto"}</h1>
            <StatusBadge status={c.status} testId="contract-status" />
          </div>
          <div className="text-sm text-[#47606B] mt-1"><span className="font-mono-it text-xs" data-testid="contract-number">{c.number}</span> • {data.evento_tipo || "evento"} {fmtValue({ type: "date" }, data.evento_data)} • {data.sede || "sede da definire"}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {editable ? (
            <span className="text-xs text-[#7B929C] inline-flex items-center gap-1" data-testid="save-indicator">
              {saveState === "saving" || saveState === "dirty" ? <><Loader2 size={13} className="animate-spin" /> Salvataggio…</> : saveState === "error" ? "Errore di salvataggio" : <><Check size={13} /> Bozza salvata</>}
            </span>
          ) : (
            <span className="text-xs text-[#47606B] inline-flex items-center gap-1" data-testid="locked-indicator"><Lock size={13} /> Contenuto bloccato</span>
          )}
          <Button variant="outline" className="rounded-full" onClick={() => openPdf(`/contracts/${id}/pdf`)} data-testid="header-pdf-button"><Printer size={15} className="mr-2" /> PDF</Button>
          {c.status !== "annullato" && <ShareButton testId="contract-share-button" doc={{ kind: "contract", id, scope: "base" }} phone={data.cliente_tel} fileName={`Contratto-${c.number}.pdf`}
            body={c.status === "firmato"
              ? `🌟 Gentile *${data.cliente_nome || "Cliente"}*,\n\n✅ le inviamo la copia del *contratto n. ${c.number}*, firmato da tutte le parti, per ${eventPhrase(data.evento_tipo)}${data.evento_data ? ` del 📅 *${fmtValue({ type: "date" }, data.evento_data)}*` : ""}.\n📁 La invitiamo a conservarla con cura.\n\n🥂 Grazie per la fiducia: sarà un piacere curare ogni dettaglio del suo evento! ✨\n\nUn cordiale saluto 🌊`
              : `🌟 Gentile *${data.cliente_nome || "Cliente"}*,\n\n📄 le inviamo il *contratto n. ${c.number}* per ${eventPhrase(data.evento_tipo)}${data.evento_data ? ` del 📅 *${fmtValue({ type: "date" }, data.evento_data)}*` : ""}, così da poterlo leggere con calma prima della firma. ✍️\n\n💬 Restiamo a disposizione per qualsiasi chiarimento.\n\nUn cordiale saluto 🌊`} />}
          <CancelContractButton c={c} reload={reload} />
        </div>
      </div>
      <CancelledBanner c={c} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <aside className="lg:col-span-3 lg:sticky lg:top-24"><Tabs tabs={tabs} tab={tab} setTab={goTab} /></aside>
        <div className="lg:col-span-9 space-y-6" key={tab}>
          {!editable && page && <div className="text-sm rounded-lg bg-[#E0F2FE] text-[#075985] p-3" data-testid="locked-banner">Il contenuto è bloccato per la firma. Per modificarlo, dalla scheda «Firma» riporta il contratto in bozza.</div>}
          {tab === "p1" && <AvailabilityAlert date={data.evento_data} sede={data.sede} contractId={id} />}
          {tab === "p5" && editable && <PresetPicker evento={data.evento_tipo} onApply={(p) => { setFields(presetPatch(p)); toast.success(`${p.nome} caricato nell'Allegato A`); }} />}
          {tab === "p5" && (editable || (data.a_menu_files || []).length > 0) && <MenuUpload cid={id} data={data} setFields={setFields} editable={editable} />}
          {page && page.sections.map((s) => (
            <SectionForm key={s.title} section={s} data={data} onField={onField} disabled={!editable}>
              {s.title.startsWith("3") && editable && (
                <Button type="button" variant="outline" size="sm" className="mt-5 rounded-full" onClick={() => update(calcPrices(data))} data-testid="calc-prices-button">
                  <Calculator size={14} className="mr-2" /> Calcola prezzo di riferimento e rate 30/40/30
                </Button>
              )}
            </SectionForm>
          ))}
          {page?.notes?.map((t) => <p key={t} className="text-xs italic text-[#47606B]">{t}</p>)}
          {tab === "start" && <IdentityStep cid={id} data={data} setFields={setFields} update={update} editable={editable} tpl={tpl} onContinue={() => goTab("p1")} />}
          {tab === "cond" && ["p2", "p3", "p4"].map((k) => <SheetView key={k} page={tpl.pages.find((p) => p.key === k)} data={data} template={tpl} />)}
          {tab === "extra" && <ExtrasPanel c={c} template={tpl} reload={reload} />}
          {tab === "firma" && <SignPanel c={c} reload={reload} />}
          {tab === "pagamenti" && <PaymentsPanel c={c} />}
          {tab === "ospiti" && <GuestsPanel c={c} reload={reload} isAdmin={user?.role === "admin"} />}
          {tab === "servizio" && <ServicePanel c={c} reload={reload} />}
          {tab === "squadra" && <TeamPanel c={c} />}
          {tab === "fornitori" && <SuppliersPanel c={c} />}
          {tab === "esperienza" && <ExperiencePanel c={c} />}
          {tab === "sala" && <SeatingPanel c={c} reload={reload} />}
          {tab === "files" && <FilesPanel cid={id} categories={tpl.file_categories} />}
          {tab === "audit" && <AuditPanel cid={id} version={version} />}
        </div>
      </div>
    </div>
  );
}
